import { Card, CardContent, Divider } from '@mui/material';
import * as React from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { ContainerEventChange, ContainerHistoryEvent } from 'services/clients/watchList';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { StatusPill } from 'pages/Shipments/components/StatusPill';

import { useSegment } from 'common/hooks/segment';
import { SegmentEvent } from 'services/helpers/segment';
import { Row } from './components/Row';
import { statusMapping } from './components/utility';

interface Props {
  event: ContainerHistoryEvent;
}

export const HistoryCard: React.FC<Props> = (props) => {
  const { event } = props;

  const [expanded, setExpanded] = React.useState(false);
  const { track } = useSegment();

  const bottomRightText = statusMapping(event);

  const eventTypeMapping: { [key: string]: string } = {
    container_availability: 'Container availability update',
    returnUpdate: 'Container return update',
    milestone: 'Container milestone',
  };

  const filterChanges = (fieldCategory: string) => {
    return event.changes?.filter(
      (change: ContainerEventChange) => change.fieldCategory === fieldCategory
    );
  };

  const pickupDetails = filterChanges('pickup');
  const demurrageDetails = filterChanges('demurrage');
  const holdDetails = filterChanges('holds');

  const occurrence = DateTime.fromISO(event.occurredAt, { zone: event.timezone });

  let bg = 'bg-white';
  switch (event.eventType) {
    case 'milestone':
      bg = 'bg-gray-50';
      break;
    default:
      break;
  }

  const expandable = event.eventType !== 'milestone';

  const handleExpand = () => {
    setExpanded(true);
    const { originalEvent, changes, ...restOfEvent } = event;
    track(SegmentEvent.ShipmentsHistoryInfoExpanded, { event: restOfEvent }, { includeUser: true });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-5 items-center p-1">
        <h2 className="text-xl font-medium">
          {occurrence.toFormat('EEE, MMM d')} at {occurrence.toFormat('h:mm a ZZZZ')}
        </h2>
        <h4 className="px-2 py-1 bg-gray-50 rounded-md font-medium">
          {eventTypeMapping[event.eventType]}
        </h4>
      </div>
      <Card variant="outlined" className={`rounded-xl ${bg}`}>
        <CardContent className="p-0 w-full h-full">
          <div
            className={`flex justify-between p-4 h-full ${
              expandable && expanded ? 'cursor-pointer' : ''
            }`}
            onClick={expandable ? () => setExpanded(false) : undefined}
          >
            <div className="flex gap-4">
              <div>
                <div className="rounded-md p-1 border border-gray-200">
                  <img
                    src={event.terminal?.logo || event.shippingLine?.logo || ''}
                    alt="logo"
                    className="w-9 h-9 object-contain"
                  />
                </div>
              </div>
              <div className="flex flex-col justify-between gap-2 leading-4">
                <h3 className="font-medium">
                  {event.eventType === 'milestone'
                    ? _.startCase(event.milestoneType)
                    : event.changesSummary}
                </h3>
                <p>{event.terminal?.port?.name || event.port?.name || ''}</p>
                <h4 className="text-gray-500">
                  {event.terminal?.name}
                  {event.terminal?.pier ? ` (${event.terminal?.pier})` : ''}
                </h4>
              </div>
            </div>
            <div className="flex flex-col justify-between items-end">
              <StatusPill status={event.eventSubtype} />
              {bottomRightText && (
                <div className="flex gap-1 items-center text-gray-900">
                  {bottomRightText.icon}
                  <h4>{bottomRightText.text}</h4>
                </div>
              )}
            </div>
          </div>
          {expandable && (
            <>
              {expanded && (
                <div>
                  <Row
                    label="Information"
                    className="bg-blue-50 font-bold border-y"
                    leftCol="Previous"
                    rightCol="Current"
                    header
                  />
                  <Row
                    label="Pickup Details"
                    className="bg-gray-50 font-medium"
                    leftCol=" "
                    rightCol=" "
                  />
                  {pickupDetails.map((detail) => (
                    <Row
                      label={detail.label}
                      leftCol={detail.leftCol}
                      rightCol={detail.rightCol}
                      tooltip={detail.tooltip}
                      key={detail.fieldKey}
                    />
                  ))}
                  <Row
                    label="Demurrage Details"
                    className="bg-gray-50 font-medium"
                    leftCol=" "
                    rightCol=" "
                  />
                  {demurrageDetails.map((detail) => (
                    <Row
                      label={detail.label}
                      leftCol={detail.leftCol}
                      rightCol={detail.rightCol}
                      tooltip={detail.tooltip}
                      key={detail.fieldKey}
                    />
                  ))}
                  {holdDetails.length > 0 && (
                    <>
                      <Row
                        label="Hold Details"
                        className="bg-gray-50 font-medium"
                        leftCol=" "
                        rightCol=" "
                      />
                      {holdDetails.map((detail) => (
                        <Row
                          label={detail.label}
                          leftCol={detail.leftCol}
                          rightCol={detail.rightCol}
                          tooltip={detail.tooltip}
                          key={detail.fieldKey}
                        />
                      ))}
                    </>
                  )}
                </div>
              )}
              {!expanded ? (
                <div className="cursor-pointer" onClick={handleExpand}>
                  <Divider className="mx-4 border-blue-300" />
                  <div className="py-4 h-14 items-center flex justify-center">
                    <div className="flex items-center justify-center text-gray-500 gap-2">
                      <h4>Expand to see change details</h4>
                      <KeyboardArrowDownIcon />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="cursor-pointer" onClick={() => setExpanded(!expanded)}>
                  <div className="py-4 h-14 items-center flex justify-center">
                    <div className="flex items-center justify-center text-gray-500 gap-2">
                      <h4>Collapse details</h4>
                      <KeyboardArrowUpIcon />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

HistoryCard.displayName = 'Shipments.DetailDrawer.Sections.History.HistoryCard';
